import React, { useState } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  DatePicker,
  Space,
  Popconfirm,
  Row,
  Col,
  Tag,
  Upload,
  Image,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
  LinkOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { 
  MentorCertificateResponse, 
  CreateMentorCertificateRequest, 
  UpdateMentorCertificateRequest 
} from '../../../types';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import {
  compressImageToDataUrl,
  fileTooLargeForCertificate,
  normalizeCertificateImagePayload,
  certificateImageToDataUrlSrc,
} from '../../../utils/certificateImage';

const { Search } = Input;

const CertificateTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState<MentorCertificateResponse | null>(null);
  const [form] = Form.useForm();
  const watchCertificateImage = Form.useWatch('certificateImage', form);
  const [imageProcessing, setImageProcessing] = useState(false);

  const { data: certificates = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.certificates,
    queryFn: async () => {
      const res = await apiService.getMentorCertificates();
      if (!res.success) throw new Error(res.message || 'Sertifikalar yüklenemedi');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMentorCertificateRequest) => apiService.createMentorCertificate(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Sertifika başarıyla eklendi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.certificates });
        setModalVisible(false);
        setEditingCertificate(null);
        form.resetFields();
      } else toast.error(res.message || 'Sertifika eklenemedi');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Sertifika eklenirken bir hata oluştu')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMentorCertificateRequest }) =>
      apiService.updateMentorCertificate(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Sertifika başarıyla güncellendi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.certificates });
        setModalVisible(false);
        setEditingCertificate(null);
        form.resetFields();
      } else toast.error(res.message || 'Sertifika güncellenemedi');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Sertifika güncellenirken bir hata oluştu')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMentorCertificate(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Sertifika başarıyla silindi');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.certificates });
      } else toast.error(res.message || 'Sertifika silinemedi');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Sertifika silinirken bir hata oluştu')),
  });

  const handleAdd = () => {
    setEditingCertificate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (certificate: MentorCertificateResponse) => {
    setEditingCertificate(certificate);
    form.setFieldsValue({
      certificateName: certificate.certificateName,
      issuingOrganization: certificate.issuingOrganization,
      certificateNumber: certificate.certificateNumber,
      issueDate: certificate.issueDate ? dayjs(certificate.issueDate) : null,
      expiryDate: certificate.expiryDate ? dayjs(certificate.expiryDate) : null,
      isExpired: certificate.isExpired,
      credentialUrl: certificate.credentialUrl,
      description: certificate.description,
      certificateImage: certificate.certificateImage
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  const handleImageUpload = async (file: File) => {
    if (fileTooLargeForCertificate(file)) {
      toast.error('Dosya çok büyük (en fazla 12 MB).');
      return false;
    }
    setImageProcessing(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      form.setFieldsValue({ certificateImage: dataUrl });
    } catch {
      toast.error('Görsel işlenemedi; farklı bir dosya deneyin.');
    } finally {
      setImageProcessing(false);
    }
    return false;
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const certificateData: CreateMentorCertificateRequest | UpdateMentorCertificateRequest = {
        certificateName: values.certificateName,
        issuingOrganization: values.issuingOrganization,
        certificateNumber: values.certificateNumber,
        issueDate: values.issueDate.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate ? values.expiryDate.format('YYYY-MM-DD') : undefined,
        isExpired: values.isExpired,
        credentialUrl: values.credentialUrl,
        description: values.description,
        certificateImage: normalizeCertificateImagePayload(values.certificateImage)
      };
      if (editingCertificate) {
        updateMutation.mutate({ id: editingCertificate.id, data: certificateData });
      } else {
        createMutation.mutate(certificateData);
      }
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingCertificate(null);
    form.resetFields();
  };

  const filteredCertificates = certificates.filter(certificate =>
    certificate.certificateName.toLowerCase().includes(searchText.toLowerCase()) ||
    (certificate.issuingOrganization && certificate.issuingOrganization.toLowerCase().includes(searchText.toLowerCase())) ||
    (certificate.certificateNumber && certificate.certificateNumber.toLowerCase().includes(searchText.toLowerCase()))
  );

  const toImageSrc = certificateImageToDataUrlSrc;

  const columns = [
    {
      title: 'Sertifika Adı',
      dataIndex: 'certificateName',
      key: 'certificateName',
      sorter: (a: MentorCertificateResponse, b: MentorCertificateResponse) =>
        a.certificateName.localeCompare(b.certificateName),
    },
    {
      title: 'Kurum',
      dataIndex: 'issuingOrganization',
      key: 'issuingOrganization',
      sorter: (a: MentorCertificateResponse, b: MentorCertificateResponse) =>
        (a.issuingOrganization || '').localeCompare(b.issuingOrganization || ''),
    },
    {
      title: 'Sertifika No',
      dataIndex: 'certificateNumber',
      key: 'certificateNumber',
      sorter: (a: MentorCertificateResponse, b: MentorCertificateResponse) =>
        (a.certificateNumber || '').localeCompare(b.certificateNumber || ''),
    },
    {
      title: 'Veriliş Tarihi',
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
      sorter: (a: MentorCertificateResponse, b: MentorCertificateResponse) =>
        new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime(),
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date: string | undefined, record: MentorCertificateResponse) => {
        if (!date) return <Tag color="green">Süresiz</Tag>;
        const expiry = dayjs(date);
        const isOver = record.isExpired || expiry.isBefore(dayjs());
        return <Tag color={isOver ? 'red' : 'green'}>{expiry.format('DD.MM.YYYY')}</Tag>;
      },
      sorter: (a: MentorCertificateResponse, b: MentorCertificateResponse) => {
        if (!a.expiryDate && !b.expiryDate) return 0;
        if (!a.expiryDate) return 1;
        if (!b.expiryDate) return -1;
        return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
      },
    },
    {
      title: 'Durum',
      dataIndex: 'isExpired',
      key: 'isExpired',
      render: (_: boolean, record: MentorCertificateResponse) => {
        if (record.isExpired) {
          return <Tag color="red">Süresi Dolmuş</Tag>;
        }
        const d = record.expiryDate ? dayjs(record.expiryDate) : null;
        if (d && d.isBefore(dayjs())) {
          return <Tag color="red">Süresi Dolmuş</Tag>;
        }
        return <Tag color="green">Geçerli</Tag>;
      },
      filters: [
        { text: 'Geçerli', value: false },
        { text: 'Süresi Dolmuş', value: true }
      ],
      onFilter: (value: boolean | React.Key, record: MentorCertificateResponse) => {
        if (value === true) {
          return record.isExpired || (!!record.expiryDate && dayjs(record.expiryDate).isBefore(dayjs()));
        }
        if (value === false) {
          return !record.isExpired && (!record.expiryDate || !dayjs(record.expiryDate).isBefore(dayjs()));
        }
        return true;
      }
    },
    {
      title: 'Görsel',
      dataIndex: 'certificateImage',
      key: 'certificateImage',
      render: (image: string | undefined) => {
        if (!image) return '—';
        return (
          <Image
            width={40}
            height={30}
            src={toImageSrc(image)}
            style={{ borderRadius: 4 }}
            preview={{ mask: <EyeOutlined /> }}
          />
        );
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorCertificateResponse) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            style={{
              backgroundColor: 'var(--text-secondary)',
              borderColor: 'var(--text-secondary)',
              color: 'white',
              fontWeight: 500
            }}
          >
            Düzenle
          </Button>
          {record.credentialUrl && (
            <Button
              type="text"
              icon={<LinkOutlined />}
              href={record.credentialUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--text-secondary)' }}
            />
          )}
          <Popconfirm
            title="Sertifikayı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
            okButtonProps={{
              style: {
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }
            }}
            cancelButtonProps={{
              style: {
                backgroundColor: 'white',
                borderColor: '#d1d5db',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }
            }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PanelTabCard
      title="Sertifikalar"
      description="Sertifika bilgilerinizi ekleyin veya düzenleyin."
    >
      <style>
        {`
          .ant-btn:not(.ant-btn-primary):hover {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):focus {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):active {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <TabListLoading loading={loading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
        </Col>
        <Col>
          <Space>
            <Search
              placeholder="Sertifika, kurum veya numara ara..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{ 
                backgroundColor: 'var(--text-secondary)', 
                borderColor: 'var(--text-secondary)' 
              }}
            >
              Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      {/* eslint-disable react/jsx-no-duplicate-props */}
      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={filteredCertificates}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />
      {/* eslint-enable react/jsx-no-duplicate-props */}
      </TabListLoading>

      <Modal
        title={editingCertificate ? 'Sertifika Düzenle' : 'Yeni Sertifika'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText="Kaydet"
        cancelText="İptal"
        okButtonProps={{
          style: {
            backgroundColor: 'var(--text-secondary)',
            borderColor: 'var(--text-secondary)',
            color: 'white',
            fontWeight: 500
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="certificateName"
                label="Sertifika Adı"
                rules={[{ required: true, message: 'Sertifika adı gereklidir' }]}
              >
                <Input placeholder="Sertifika adını girin" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="issuingOrganization"
                label="Kurum"
              >
                <Input placeholder="Sertifika veren kurum" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="certificateNumber"
                label="Sertifika Numarası"
              >
                <Input placeholder="Sertifika numarası" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="credentialUrl" label="Doğrulama Linki">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="issueDate"
                label="Veriliş Tarihi"
                rules={[
                  { required: true, message: 'Veriliş tarihi gereklidir' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Veriliş tarihi bugünden sonra olamaz'));
                      }
                      const expiryDate = form.getFieldValue('expiryDate');
                      if (expiryDate && selectedDate.isAfter(dayjs(expiryDate))) {
                        return Promise.reject(new Error('Veriliş tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expiryDate"
                label="Bitiş Tarihi"
                dependencies={['issueDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const issueDate = form.getFieldValue('issueDate');
                      if (issueDate && dayjs(value).isBefore(dayjs(issueDate))) {
                        return Promise.reject(new Error('Bitiş tarihi veriliş tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Süresiz bırakılabilir"
                  disabledDate={(current) => {
                    const issueDate = form.getFieldValue('issueDate');
                    if (issueDate && current) {
                      return current.isBefore(dayjs(issueDate), 'day');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="isExpired" label="Süresi Dolmuş" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item label="Sertifika Görseli">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
              disabled={imageProcessing}
            >
              <Button icon={<FileTextOutlined />} loading={imageProcessing} disabled={imageProcessing}>
                {imageProcessing ? 'İşleniyor…' : 'Görsel Yükle'}
              </Button>
            </Upload>
            {watchCertificateImage && (
              <div style={{ marginTop: 8 }}>
                <Image
                  width={100}
                  height={75}
                  src={toImageSrc(watchCertificateImage)}
                  style={{ borderRadius: 4 }}
                />
                <div style={{ marginTop: 8 }}>
                  <Button size="small" onClick={() => form.setFieldsValue({ certificateImage: undefined })}>
                    Görseli Kaldır
                  </Button>
                </div>
              </div>
            )}
          </Form.Item>
          <Form.Item name="certificateImage" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} placeholder="Sertifika hakkında açıklama" />
          </Form.Item>
        </Form>
      </Modal>
    </PanelTabCard>
  );
};

export default CertificateTab;
